import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Moon,
  Search,
  Sun,
  Menu,
  LogOut,
  User,
  Settings as SettingsIcon
} from "lucide-react";

const titles = {
  "/": "Dashboard",
  "/about": "About Me",
  "/personal": "Personal Information",
  "/education": "Education",
  "/experience": "Experience",
  "/skills": "Technical Skills",
  "/expertise": "Expertise",
  "/achievements": "Achievements",
  "/testimonials": "Testimonials",
  "/blogs": "Blogs",
  "/interests": "Interests",
  "/social": "Social Links",
  "/settings": "Site Settings"
};

export default function TopNav({ onMenuClick }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const title = titles[location.pathname] || "Folio Admin";

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <header
      data-testid="topnav"
      className="sticky top-0 z-30 h-16 border-b border-border bg-background/80 glass"
    >
      <div className="flex h-full items-center gap-3 px-4 sm:px-6">
        <button
          data-testid="mobile-menu-btn"
          onClick={onMenuClick}
          className="lg:hidden inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5" strokeWidth={1.5} />
        </button>

        <div className="flex flex-col min-w-0">
          <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Folio Admin
          </span>
          <h1
            data-testid="page-title"
            className="font-display text-lg font-semibold tracking-tight truncate"
          >
            {title}
          </h1>
        </div>

        <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
          <div className="relative hidden md:block">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none"
              strokeWidth={1.5}
            />
            <Input
              data-testid="global-search"
              placeholder="Search across portfolio…"
              className="pl-9 w-[260px] xl:w-[340px] h-9 bg-muted/40 border-border focus-visible:bg-background"
            />
            <kbd className="absolute right-2.5 top-1/2 -translate-y-1/2 hidden xl:inline-flex h-5 items-center rounded border border-border bg-background px-1.5 font-mono text-[10px] text-muted-foreground">
              ⌘K
            </kbd>
          </div>

          <Button
            data-testid="theme-toggle"
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="h-9 w-9"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? (
              <Sun className="h-[18px] w-[18px]" strokeWidth={1.5} />
            ) : (
              <Moon className="h-[18px] w-[18px]" strokeWidth={1.5} />
            )}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="profile-dropdown-trigger"
                className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-muted transition-colors"
              >
                <Avatar className="h-7 w-7">
                  <AvatarImage src={user?.profilePicture || user?.avatar} alt={user?.name} />
                  <AvatarFallback className="text-xs bg-primary text-primary-foreground">
                    {user?.name?.split(" ").map((n) => n[0]).join("") || "A"}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden sm:inline text-sm font-medium">
                  {user?.name || "Admin"}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span className="text-sm font-semibold">{user?.name}</span>
                  <span className="text-xs text-muted-foreground font-normal">
                    {user?.email}
                  </span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="dropdown-profile"
                onClick={() => navigate("/personal")}
              >
                <User className="mr-2 h-4 w-4" strokeWidth={1.5} />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="dropdown-settings"
                onClick={() => navigate("/settings")}
              >
                <SettingsIcon className="mr-2 h-4 w-4" strokeWidth={1.5} />
                Site Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="dropdown-logout"
                onClick={handleLogout}
                className="text-destructive focus:text-destructive"
              >
                <LogOut className="mr-2 h-4 w-4" strokeWidth={1.5} />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
